import format from 'date-fns/format'
import { FC, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import s from './MyCampaignCard.module.scss'

import { Campaign } from 'types/campaign'

import { STATUS_NAME_BY_STATUS_VALUE, STATUS_STYLE_BY_STATUS_VALUE } from 'constants/campaignStatusConsts'

import transformUTCToLocale from 'utils/transformUTCToLocale'

import { useDownloadFile } from 'hooks/useDownloadFile'
import { useModal } from 'hooks/useModal'
import { useShowDropdown } from 'hooks/useShowDropdown'

import { advertiserDashboardApi } from 'api/advertiserDashboard-api'

import { ClockIcon } from 'assets/icons/ClockIcon'
import { DeleteIcon } from 'assets/icons/DeleteIcon'
import { DownloadIcon } from 'assets/icons/DownloadIcon'
import { EditIcon } from 'assets/icons/EditIcon'
import { HeatmapIcon } from 'assets/icons/HeatmapIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'

import { Card } from 'components/UI/Card/Card'
import ConfirmMessage from 'components/UI/ConfirmMessage'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { Dropmenu } from 'components/UI/Dropmenu/Dropmenu'
import { Status } from 'components/UI/Status/Status'

interface MyCampaignCardProps {
  campaign: Campaign
  deleteCampaign: (id: number) => void
}

const MyCampaignCard: FC<MyCampaignCardProps> = ({ campaign, deleteCampaign }) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { ref, showDropdown } = useShowDropdown()
  const { show, handleClose, handleOpen } = useModal()

  const [isAllowedDelete, setIsAllowedDelete] = useState<boolean>(false)
  const [isAllowedEdit, setIsAllowedEdit] = useState<boolean>(false)
  useEffect(() => {
    setIsAllowedDelete(campaign.status < 10 || campaign.status >= 30)
    setIsAllowedEdit(campaign.status === 0)
  }, [campaign])

  const openCampaignInfoModal = () => {
    navigate(`${campaign.id}/?${searchParams}`, { replace: true })
  }

  const deleteCampaignHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    handleOpen() // opens confirm modal
  }

  const [downloadReport] = useDownloadFile({
    getData: () => advertiserDashboardApi.downloadReport(campaign.id, 'ad-campaign'),
  })

  return (
    <>
      <Card variant="secondary" className={s.wrapper} onClick={openCampaignInfoModal}>
        <Card.Info className={s.cardInfo}>
          <div className={s.info}>
            <div className={s.headerWrapper}>
              <Card.CroppedText>{campaign.name}</Card.CroppedText>
              <Status
                label={STATUS_NAME_BY_STATUS_VALUE[campaign.status]}
                color={STATUS_STYLE_BY_STATUS_VALUE[campaign.status]}
              />
            </div>
            <div className={s.description}>
              <span className={s.date}>
                <ClockIcon className={s.dateIcon} />
                <div>
                  {campaign.start_datetime
                    ? format(transformUTCToLocale(campaign?.start_datetime), 'MMM dd yyyy HH:mm')
                    : null}
                  {' to '}
                </div>
                <div>
                  {campaign.finish_datetime
                    ? format(transformUTCToLocale(campaign?.finish_datetime), 'MMM dd yyyy HH:mm')
                    : null}
                </div>
              </span>
              {!(
                campaign.has_scene &&
                (campaign.status === 12 || campaign.status === 13 || campaign.status === 20)
              ) ? null : (
                <CustomButton
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`heatmap/${campaign.id}?${searchParams}`, { replace: true })
                  }}
                  small
                  className={s.heatmapButton}
                >
                  <HeatmapIcon className={s.heatmapIcon} />
                </CustomButton>
              )}
            </div>
          </div>
          <div className={s.buttonsWrapper}>
            <CustomButton variant="secondary" onClick={openCampaignInfoModal} small>
              <InfoIcon className={s.infoIcon} />
            </CustomButton>
            {isAllowedEdit ||
            isAllowedDelete ||
            campaign.status === 12 ||
            campaign.status === 13 ||
            campaign.status === 20 ? (
              <Dropmenu
                variant="secondary"
                small
                show={showDropdown}
                cref={ref}
                items={[
                  {
                    icon: <EditIcon />,
                    title: 'Edit',
                    onClick: () => navigate(`edit/${campaign.id}`),
                    hide: !isAllowedEdit,
                  },
                  {
                    icon: <DeleteIcon />,
                    title: 'Delete',
                    onClick: (e) => deleteCampaignHandler(e),
                    hide: !isAllowedDelete,
                  },
                  {
                    icon: <HeatmapIcon />,
                    title: 'Heatmap',
                    onClick: () => navigate(`heatmap/${campaign.id}?${searchParams}`, { replace: true }),
                    hide: !(
                      campaign.has_scene &&
                      (campaign.status === 12 || campaign.status === 13 || campaign.status === 20)
                    ),
                  },
                  {
                    icon: <DownloadIcon />,
                    title: 'Download Report',
                    onClick: () => downloadReport(),
                    hide: !(campaign.status === 12 || campaign.status === 13 || campaign.status === 20),
                  },
                ]}
              />
            ) : null}
          </div>
        </Card.Info>
      </Card>
      <>
        <ConfirmMessage
          isOpen={show}
          handleClose={handleClose}
          message={`Are you sure you want to delete the "${campaign.name}" campaign?`}
          deleteItem={() => deleteCampaign(campaign.id)}
          id={campaign.id}
        />
      </>
    </>
  )
}

export default MyCampaignCard
